import type {
  BrowserAgentTaskCreate,
  FiscalFactReviewGroup,
  FiscalFactReviewGroupDetail,
  FiscalFactReviewItem,
  FiscalFactReviewSample,
  FiscalFactReviewSession,
  FiscalFactReviewSessionAction,
} from "@backchannel/tax-contracts";
import {
  FISCAL_FACT_SAMPLE_MAX_EXPANSIONS,
  FISCAL_FACT_SAMPLE_WHOLE_GROUP_MAX,
} from "@backchannel/tax-contracts";
import { formatNcmCode, percentVariants } from "./fiscal-facts";

/**
 * Pure helpers for the group review desk (Regras → Verificação). The API is
 * the authority on groups, samples and confirmations; these functions only
 * shape what the desk shows and predict the session between round trips.
 */

/* -- Evidence: quote ↔ field binding ------------------------------------------- */

export type EvidenceFieldId =
  | "cest"
  | "ncm"
  | "originalMva"
  | "destinationInternalRate"
  | "interstateRate"
  | "validFrom"
  | "stApplicable";

export type EvidenceFieldEntry = {
  id: EvidenceFieldId;
  /** Strings the source may print for this field, most specific first. */
  needles: string[];
};

export type FieldSpan = { start: number; end: number };

/**
 * One span per field, bound by reading order: an annex row prints CEST, NCM,
 * description, MVA, rate and validity left to right, so each field is looked
 * for after the previous field's span. The same number elsewhere in the row
 * is never marked. A field whose value is not in the quote has no span.
 */
export function fieldSpans(
  quote: string,
  entries: readonly EvidenceFieldEntry[],
): Map<EvidenceFieldId, FieldSpan> {
  const spans = new Map<EvidenceFieldId, FieldSpan>();
  const lower = quote.toLocaleLowerCase("pt-BR");
  const taken: FieldSpan[] = [];
  const overlaps = (span: FieldSpan) =>
    taken.some((other) => span.start < other.end && other.start < span.end);
  const find = (needles: string[], from: number): FieldSpan | null => {
    let best: FieldSpan | null = null;
    for (const needle of [...needles].sort((a, b) => b.length - a.length)) {
      if (needle.length < 2) continue;
      const target = needle.toLocaleLowerCase("pt-BR");
      let index = lower.indexOf(target, from);
      while (index >= 0) {
        const span = { start: index, end: index + target.length };
        // A value must not be the tail of a longer number ("5,00" inside "45,00").
        const before = quote[index - 1] ?? "";
        const after = quote[span.end] ?? "";
        const boundary = !/[\d.,]/.test(before) && !/\d/.test(after);
        if (boundary && !overlaps(span)) {
          if (!best || span.start < best.start) best = span;
          break;
        }
        index = lower.indexOf(target, index + 1);
      }
    }
    return best;
  };
  let cursor = 0;
  for (const entry of entries) {
    const span = find(entry.needles, cursor) ?? find(entry.needles, 0);
    if (!span) continue;
    spans.set(entry.id, span);
    taken.push(span);
    cursor = Math.max(cursor, span.end);
  }
  return spans;
}

export type QuoteSegment = { text: string; field: EvidenceFieldId | null };

/** Splits a quote at the field spans (for rendering marks). */
export function quoteSegments(
  quote: string,
  spans: ReadonlyMap<EvidenceFieldId, FieldSpan>,
): QuoteSegment[] {
  const ordered = [...spans.entries()].sort((a, b) => a[1].start - b[1].start);
  const out: QuoteSegment[] = [];
  let index = 0;
  for (const [field, span] of ordered) {
    if (span.start > index) out.push({ text: quote.slice(index, span.start), field: null });
    out.push({ text: quote.slice(span.start, span.end), field });
    index = span.end;
  }
  if (index < quote.length) out.push({ text: quote.slice(index), field: null });
  return out;
}

function isoToBr(date: string): string {
  const [year, month, day] = date.split("-");
  return `${day}/${month}/${year}`;
}

/** Fields a review item states, in the order an annex row prints them. */
export function evidenceFieldEntries(item: {
  fact: FiscalFactReviewItem["fact"];
  evidence: Pick<FiscalFactReviewItem["evidence"], "displayed">;
}): EvidenceFieldEntry[] {
  const { fact, evidence } = item;
  const shown = evidence.displayed;
  const entries: EvidenceFieldEntry[] = [];
  const cest = shown.cest ?? fact.match.cest ?? undefined;
  if (cest) entries.push({ id: "cest", needles: [cest] });
  const ncms = [
    ...(shown.ncmCited ?? []),
    ...(fact.match.ncm ? [formatNcmCode(fact.match.ncm), fact.match.ncm] : []),
    ...(fact.match.ncmPrefixes ?? []).flatMap((prefix) =>
      prefix.length === 8 ? [formatNcmCode(prefix), prefix] : prefix.length >= 4 ? [prefix] : [],
    ),
  ];
  if (ncms.length) entries.push({ id: "ncm", needles: [...new Set(ncms)] });
  const rate = (field: "originalMva" | "destinationInternalRate" | "interstateRate") => {
    const value = fact.fields[field];
    if (value === undefined) return;
    const displayed =
      field === "originalMva"
        ? shown.originalMva
        : field === "destinationInternalRate"
          ? (shown.internalRate ?? (fact.kind === "INTERNAL_RATE" ? shown.rate : undefined))
          : fact.kind === "INTERSTATE_RATE"
            ? shown.rate
            : undefined;
    const variants = percentVariants(value);
    entries.push({
      id: field,
      needles: [
        ...(displayed ? [displayed.endsWith("%") ? displayed : `${displayed}%`, displayed] : []),
        ...variants,
        ...variants.map((variant) => variant.replace(/\s?%$/, "")),
      ],
    });
  };
  rate("originalMva");
  rate("destinationInternalRate");
  rate("interstateRate");
  if (fact.validFromStated) {
    entries.push({
      id: "validFrom",
      needles: [...(shown.validFrom ? [shown.validFrom] : []), isoToBr(fact.validFrom)],
    });
  }
  return entries;
}

export type QuoteLocation = {
  /** UTF-16 index of the quote in the page text. */
  index: number;
  /** UTF-16 index right after it (a folded match may be longer or shorter). */
  end: number;
  /** False when only whitespace, invisible characters or Unicode forms differ. */
  exact: boolean;
};

/*
 * Characters official HTML/PDF text scatters inside words and numbers
 * (soft hyphen, zero-width space/joiners, word joiner, BOM). They carry no
 * text: the worker ignores them when it builds the locator, so do we.
 */
const IGNORABLE = /\u00ad|\u200b|\u200c|\u200d|\u2060|\ufeff/u;
const COMBINING = /\p{M}/u;

/** `map[i]` / `ends[i]`: UTF-16 start / end in the source of folded character i. */
type Folded = { text: string; map: number[]; ends: number[] };

/**
 * A comparable form of `value` and, per folded character, its UTF-16 index
 * in `value`: invisible characters dropped, NFKC per character cluster
 * (composed and decomposed accents, NBSP, ligatures compare equal) and
 * whitespace runs collapsed to one space — or removed with `spaces: false`,
 * which also tolerates a space PDF/Word text inserts inside a word.
 */
function fold(value: string, spaces: boolean): Folded {
  const out: string[] = [];
  const map: number[] = [];
  const ends: number[] = [];
  let index = 0;
  let pendingSpace = false;
  while (index < value.length) {
    const codePoint = value.codePointAt(index) as number;
    const char = String.fromCodePoint(codePoint);
    let next = index + char.length;
    if (IGNORABLE.test(char)) {
      index = next;
      continue;
    }
    if (/\s/u.test(char)) {
      pendingSpace = out.length > 0;
      index = next;
      continue;
    }
    // A base character and the combining marks that follow it form one cluster.
    let cluster = char;
    while (next < value.length) {
      const following = String.fromCodePoint(value.codePointAt(next) as number);
      if (!COMBINING.test(following)) break;
      cluster += following;
      next += following.length;
    }
    if (pendingSpace && spaces) {
      out.push(" ");
      map.push(index);
      ends.push(index);
    }
    pendingSpace = false;
    for (const part of cluster.normalize("NFKC")) {
      if (/\s/u.test(part)) {
        if (spaces) {
          out.push(" ");
          map.push(index);
          ends.push(next);
        }
        continue;
      }
      out.push(part);
      map.push(index);
      ends.push(next);
    }
    index = next;
  }
  map.push(value.length);
  return { text: out.join(""), map, ends };
}

function nearest(haystack: string, needle: string, expected: number): number | null {
  let best: number | null = null;
  let index = haystack.indexOf(needle);
  while (index >= 0) {
    if (best === null || Math.abs(index - expected) < Math.abs(best - expected)) best = index;
    index = haystack.indexOf(needle, index + 1);
  }
  return best;
}

/**
 * Where a quote sits in a page of the preserved text: the verbatim
 * occurrence closest to the locator's expected index (exact), else the
 * closest match that differs only in whitespace, line breaks, invisible
 * characters or Unicode normalization (the same tolerance the worker used
 * to build the locator). Null when the page does not hold the quote — a
 * true discrepancy.
 */
export function locateQuote(text: string, quote: string, expected: number): QuoteLocation | null {
  if (!quote) return null;
  const exact = nearest(text, quote, expected);
  if (exact !== null) return { index: exact, end: exact + quote.length, exact: true };
  for (const spaces of [true, false]) {
    const folded = fold(quote, spaces).text;
    if (!folded) return null;
    const page = fold(text, spaces);
    // The folded index nearest to the expected raw index.
    let expectedFolded = page.map.findIndex((raw) => raw >= expected);
    if (expectedFolded < 0) expectedFolded = page.text.length;
    const found = nearest(page.text, folded, expectedFolded);
    if (found !== null) {
      return {
        index: page.map[found] as number,
        end: page.ends[found + folded.length - 1] ?? text.length,
        exact: false,
      };
    }
  }
  return null;
}

/** UTF-16 index of a code-point offset in `text` (locators count code points). */
export function codePointIndex(text: string, codePoints: number): number {
  let index = 0;
  let count = 0;
  while (index < text.length && count < codePoints) {
    index += (text.codePointAt(index) as number) > 0xffff ? 2 : 1;
    count += 1;
  }
  return index;
}

/**
 * The artifact page to fetch around a code-point locator: it starts `pad`
 * code points before the quote (`charOffset`, the locator's own unit — a
 * byte offset would land thousands of characters early in accented text)
 * and asks for enough UTF-8 bytes (up to 4 per code point) to hold the pad,
 * the quote and the pad after it.
 */
export function artifactWindow(
  locator: { textStart: number; textEnd: number },
  pad: number,
): { charOffset: number; limit: number } {
  const charOffset = Math.max(0, locator.textStart - pad);
  const length = Math.max(0, locator.textEnd - locator.textStart);
  const codePoints = locator.textStart - charOffset + length + pad;
  return { charOffset, limit: Math.max(1, Math.min(256 * 1024, codePoints * 4)) };
}

/**
 * The quote in a fetched page: `page.charOffset` is the code-point offset of
 * `page.text`, so the locator gives the exact expected index.
 */
export function locateInPage(
  page: { text: string; charOffset?: number },
  locator: { textStart: number },
  quote: string,
): QuoteLocation | null {
  const expected =
    page.charOffset === undefined
      ? 0
      : codePointIndex(page.text, Math.max(0, locator.textStart - page.charOffset));
  return locateQuote(page.text, quote, expected);
}

/* -- Session: optimistic reducer -------------------------------------------------- */

/** Mirrors the API's session actions so the desk updates before the round trip. */
export function applySessionAction(
  session: FiscalFactReviewSession,
  action: FiscalFactReviewSessionAction,
  now: string,
): FiscalFactReviewSession {
  switch (action.action) {
    case "INSPECT": {
      const note = action.note?.trim() || null;
      const inspected = [
        ...session.inspected.filter((entry) => entry.factId !== action.factId),
        {
          factId: action.factId,
          revision: action.revision,
          outcome: action.outcome,
          note,
          at: now,
        },
      ];
      if (action.outcome === "DISCREPANCY") {
        return {
          ...session,
          inspected,
          exceptions: [
            ...session.exceptions.filter((entry) => entry.factId !== action.factId),
            {
              factId: action.factId,
              reason: note ? `Discrepância: ${note}` : "Discrepância",
              at: now,
            },
          ],
          sampleExpansions:
            session.method === "SAMPLE"
              ? Math.min(FISCAL_FACT_SAMPLE_MAX_EXPANSIONS, session.sampleExpansions + 1)
              : session.sampleExpansions,
        };
      }
      return {
        ...session,
        inspected,
        exceptions: session.exceptions.filter(
          (entry) => entry.factId !== action.factId || !entry.reason.startsWith("Discrepância"),
        ),
      };
    }
    case "CLEAR_INSPECTION":
      return {
        ...session,
        inspected: session.inspected.filter((entry) => entry.factId !== action.factId),
      };
    case "MARK_EXCEPTION":
      return {
        ...session,
        exceptions: [
          ...session.exceptions.filter((entry) => entry.factId !== action.factId),
          { factId: action.factId, reason: action.reason.trim(), at: now },
        ],
      };
    case "CLEAR_EXCEPTION":
      return {
        ...session,
        exceptions: session.exceptions.filter((entry) => entry.factId !== action.factId),
        inspected: session.inspected.filter(
          (entry) => entry.factId !== action.factId || entry.outcome !== "DISCREPANCY",
        ),
      };
    case "SET_METHOD":
      return { ...session, method: action.method };
    case "EXPAND_SAMPLE":
      return {
        ...session,
        sampleExpansions: Math.min(FISCAL_FACT_SAMPLE_MAX_EXPANSIONS, session.sampleExpansions + 1),
      };
    default:
      return session;
  }
}

/* -- Desk state ------------------------------------------------------------------- */

export type FactReviewMark = "AGREES" | "DISCREPANCY" | "EXCEPTION" | "PENDING";

export function factReviewMark(
  factId: string,
  revision: number,
  session: Pick<FiscalFactReviewSession, "inspected" | "exceptions"> | null,
): FactReviewMark {
  if (!session) return "PENDING";
  const inspection = session.inspected.find((entry) => entry.factId === factId);
  if (inspection?.outcome === "DISCREPANCY") return "DISCREPANCY";
  if (session.exceptions.some((entry) => entry.factId === factId)) return "EXCEPTION";
  if (inspection?.outcome === "AGREES" && inspection.revision === revision) return "AGREES";
  return "PENDING";
}

export const DESK_FILTERS = ["all", "sample", "pending", "exceptions", "flagged"] as const;
export type DeskFilter = (typeof DESK_FILTERS)[number];

export function deskItems(
  items: readonly FiscalFactReviewItem[],
  filter: DeskFilter,
  session: FiscalFactReviewSession | null,
  sample: Pick<FiscalFactReviewSample, "factIds">,
  method: "FULL" | "SAMPLE",
): FiscalFactReviewItem[] {
  const inSample = new Set(sample.factIds);
  return items.filter((item) => {
    const mark = factReviewMark(item.fact.id, item.fact.revision, session);
    switch (filter) {
      case "sample":
        return inSample.has(item.fact.id);
      case "pending":
        return mark === "PENDING" && (method === "FULL" || inSample.has(item.fact.id));
      case "exceptions":
        return mark === "EXCEPTION" || mark === "DISCREPANCY";
      case "flagged":
        return item.flags.length > 0;
      default:
        return true;
    }
  });
}

/** J / K: the next or previous id, clamped (never wraps past the ends). */
export function stepFact(ids: readonly string[], current: string | undefined, delta: 1 | -1) {
  if (ids.length === 0) return undefined;
  const index = current ? ids.indexOf(current) : -1;
  if (index < 0) return ids[0];
  return ids[Math.min(ids.length - 1, Math.max(0, index + delta))];
}

/** After agreeing: the next fact still needing an inspection, else the next one. */
export function nextToReview(
  ids: readonly string[],
  current: string,
  needsReview: (id: string) => boolean,
): string | undefined {
  const start = ids.indexOf(current);
  for (let offset = 1; offset <= ids.length; offset += 1) {
    const id = ids[(start + offset) % ids.length];
    if (id && id !== current && needsReview(id)) return id;
  }
  return stepFact(ids, current, 1);
}

/** Default method: sample a document group bigger than a whole-group sample. */
export function defaultReviewMethod(group: Pick<FiscalFactReviewGroup, "key" | "counts">) {
  return group.key.mode === "DOCUMENT" && group.counts.pending > FISCAL_FACT_SAMPLE_WHOLE_GROUP_MAX
    ? "SAMPLE"
    : "FULL";
}

/* -- Keyboard ----------------------------------------------------------------------- */

export type DeskShortcut =
  | "next"
  | "previous"
  | "agree"
  | "evidence"
  | "exception"
  | "decision"
  | "escape"
  | "help";

type KeyEventLike = {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  target: EventTarget | null;
  isComposing?: boolean;
};

function isTypingTarget(target: EventTarget | null): boolean {
  const element = target as { tagName?: string; isContentEditable?: boolean } | null;
  if (!element || typeof element.tagName !== "string") return false;
  const tag = element.tagName.toLowerCase();
  return (
    tag === "input" || tag === "textarea" || tag === "select" || element.isContentEditable === true
  );
}

/**
 * Desk shortcuts, scoped by the caller to the desk. Never inside a field,
 * never with ⌘/Ctrl/Alt, never while composing. Enter only opens the
 * decision review; confirming needs its own step in the dialog.
 */
export function deskShortcut(event: KeyEventLike): DeskShortcut | null {
  if (event.isComposing || event.metaKey || event.ctrlKey || event.altKey) return null;
  if (event.key === "Escape") return "escape";
  if (isTypingTarget(event.target)) return null;
  switch (event.key) {
    case "j":
    case "J":
    case "ArrowDown":
      return "next";
    case "k":
    case "K":
    case "ArrowUp":
      return "previous";
    case " ":
      return "agree";
    case "e":
    case "E":
      return "evidence";
    case "x":
    case "X":
      return "exception";
    case "Enter":
      return "decision";
    case "?":
      return "help";
    default:
      return null;
  }
}

/* -- IOB corroboration ------------------------------------------------------------- */

/** One LIST task per route of the plan (existing IOB queue API). */
export function iobCorroborationTasks(
  detail: Pick<FiscalFactReviewGroupDetail, "iobPlan" | "group">,
  name: (route: { originUf: string; destinationUf: string }) => string,
): BrowserAgentTaskCreate[] {
  const { iobPlan } = detail;
  if (!iobPlan.queueable || iobPlan.ncms.length === 0) return [];
  return iobPlan.routes.map((route) => ({
    schemaVersion: "1.0.0",
    name: name(route).slice(0, 120),
    scope: { kind: "LIST", ncms: [...iobPlan.ncms] },
    originUf: route.originUf,
    destinationUf: route.destinationUf,
    skipCovered: true,
    placement: "END",
  }));
}

export function corroborationSummary(items: readonly FiscalFactReviewItem[], ids?: Set<string>) {
  let match = 0;
  let mismatch = 0;
  let none = 0;
  for (const item of items) {
    if (ids && !ids.has(item.fact.id)) continue;
    if (item.corroboration.state === "MATCH") match += 1;
    else if (item.corroboration.state === "MISMATCH") mismatch += 1;
    else none += 1;
  }
  return { match, mismatch, none };
}
